import { MentionCustomizationModel } from '../../Shared/models/mention-customization.model';
import { MonitoringTextStyleModel } from '../../Shared/models/monitoring-text-style.model';
import { MonitoringDateStyleModel } from '../../Shared/models/monitoring-date-style-model';
import { GenericStyleModel } from '../../../Common/Styles/models/generic-style.model';
import { MonitoringImageStyleModel } from '../../Shared/models/monitoring-image-style.model';
import { MonitoringLayoutStyle } from '../../Shared/models/monitoring-layout-style.model';
import { LayoutBlockDisplayRatiosEnum } from '../../../Common/PageBuilder/enums/layout-block-display-ratios.enum';
import { MonitoringDividerStyleModel } from '../../Shared/models/monitoring-divider-style.model';
import { SocialLinkStyleModel } from '../../../Common/PageBuilder/models/link-style.model';
import { MonitoringTitleStyleModel } from '../../Shared/models/monitoring-title-style.model';
import { CuratorStyle } from '../../../Common/PageBuilder/models/curator-style.model';

const defaultBriefingAccentColor = '#0D74BA';

export class BriefingGeneralStylesModel {
    mention: MentionCustomizationModel;
    title: MonitoringTitleStyleModel;
    text: MonitoringTextStyleModel;
    date: MonitoringDateStyleModel;
    tableOfContent: MonitoringTextStyleModel;
    image: MonitoringImageStyleModel;
    oneColumnLayout: MonitoringLayoutStyle;
    twoColumnLayout: MonitoringLayoutStyle;
    threeColumnLayout: MonitoringLayoutStyle;
    analysis: MonitoringTextStyleModel;
    divider: MonitoringDividerStyleModel;
    link: SocialLinkStyleModel;
    chart: MonitoringImageStyleModel;
    coverageCurator: CuratorStyle;
    accentColor?: string = defaultBriefingAccentColor;

    constructor(apiModel?: any) {
        if (apiModel) {
            this.setStyles(apiModel);
            this.accentColor = apiModel.accentColor;
        }

        if (!this.mention) {
            this.mention = new MentionCustomizationModel();
        }
        if (!this.title) {
            this.title = new MonitoringTitleStyleModel();
            this.title.color = '#5e6473';
        }
        if (!this.text) {
            this.text = new MonitoringTextStyleModel();
        }
        if (!this.date) {
            this.date = new MonitoringDateStyleModel();
        }
        if (!this.tableOfContent) {
            this.tableOfContent = new MonitoringTextStyleModel();
            this.tableOfContent.color = '#005494';
        }
        if (!this.image) {
            this.image = new MonitoringImageStyleModel();
        }
        if (!this.oneColumnLayout) {
            this.oneColumnLayout = new MonitoringLayoutStyle();
        }
        if (!this.twoColumnLayout) {
            this.twoColumnLayout = new MonitoringLayoutStyle(null, LayoutBlockDisplayRatiosEnum.ONE_TO_ONE);
        }
        if (!this.threeColumnLayout) {
            this.threeColumnLayout = new MonitoringLayoutStyle(null, LayoutBlockDisplayRatiosEnum.ONE_TO_ONE_TO_ONE);
        }
        if (!this.analysis) {
            this.analysis = new MonitoringTextStyleModel();
        }
        if (!this.divider) {
            this.divider = new MonitoringDividerStyleModel();
        }
        if (!this.link) {
            this.link = new SocialLinkStyleModel();
        }

        if (!this.chart) {
            this.chart = new MonitoringImageStyleModel();
        }

        if (!this.accentColor) {
            this.accentColor = defaultBriefingAccentColor;
        }

        if (!this.coverageCurator) {
            this.coverageCurator = new CuratorStyle();
        }
    }

    toApi(): any {
        return {
            'mention': this.mention.toBlockStyle(),
            'title': this.title.toBlockStyle(),
            'text': this.text.toBlockStyle(),
            'date': this.date.toBlockStyle(),
            'tableOfContent': this.tableOfContent.toBlockStyle(),
            'image': this.image.toBlockStyle(),
            'oneColumnLayout': this.oneColumnLayout.toBlockStyle(),
            'twoColumnLayout': this.twoColumnLayout.toBlockStyle(),
            'threeColumnLayout': this.threeColumnLayout.toBlockStyle(),
            'analysis': this.analysis.toBlockStyle(),
            'divider': this.divider.toBlockStyle(),
            'link': this.link.toBlockStyle(),
            'chart': this.chart.toBlockStyle(),
            'coverageCurator': this.coverageCurator.toBlockStyle(),
            'accentColor': this.accentColor ? this.accentColor : defaultBriefingAccentColor
        };
    }

    /**
     * Setter for global styles properties
     */
    setStyles(styles: any): void {
        if (!styles) {
            return;
        }
        if (styles.hasOwnProperty('mention')) {
            this.mention = new MentionCustomizationModel(styles.mention);
        }
        if (styles.hasOwnProperty('title')) {
            this.title = new MonitoringTitleStyleModel(styles.title);
        }
        if (styles.hasOwnProperty('text')) {
            this.text = new MonitoringTextStyleModel(styles.text);
        }
        if (styles.hasOwnProperty('date')) {
            this.date = new MonitoringDateStyleModel(styles.date);
        }
        if (styles.hasOwnProperty('tableOfContent')) {
            this.tableOfContent = new MonitoringTextStyleModel(styles.tableOfContent);
        }
        if (styles.hasOwnProperty('image')) {
            this.image = new MonitoringImageStyleModel(styles.image);
        }
        if (styles.hasOwnProperty('oneColumnLayout')) {
            this.oneColumnLayout = new MonitoringLayoutStyle(styles.oneColumnLayout);
        }
        if (styles.hasOwnProperty('twoColumnLayout')) {
            this.twoColumnLayout = new MonitoringLayoutStyle(styles.twoColumnLayout);
        }
        if (styles.hasOwnProperty('threeColumnLayout')) {
            this.threeColumnLayout = new MonitoringLayoutStyle(styles.threeColumnLayout);
        }
        if (styles.hasOwnProperty('analysis')) {
            this.analysis = new MonitoringTextStyleModel(styles.analysis);
        }
        if (styles.hasOwnProperty('divider')) {
            this.divider = new MonitoringDividerStyleModel(styles.divider);
        }
        if (styles.hasOwnProperty('link')) {
            this.link = new SocialLinkStyleModel(styles.link);
        }
        if (styles.hasOwnProperty('chart')) {
            this.chart = new MonitoringImageStyleModel(styles.chart);
        }
        if (styles.hasOwnProperty('coverageCurator')) {
            this.coverageCurator = new CuratorStyle(styles.coverageCurator);
        }
        if (styles.hasOwnProperty('accentColor')) {
            this.accentColor = styles.accentColor;
        }
    }

    /**
     * Gets the styles as their generic model for iterating
     */
    getStyles(): GenericStyleModel[] {
        return [
            this.mention,
            this.title,
            this.text,
            this.date,
            this.tableOfContent,
            this.image,
            this.oneColumnLayout,
            this.twoColumnLayout,
            this.threeColumnLayout,
            this.analysis,
            this.divider,
            this.link,
            this.chart,
            this.coverageCurator
        ];
    }
    /**
     * Setter for nested properties in this model
     */
    setProperty(property: string, value: any): void {
        const parts = property.split('.');
        let obj: any = this;
        for (let i = 0, l = parts.length; i < l - 1; i++) {
            obj = obj[parts[i]];
        }
        obj[parts[parts.length - 1]] = value;
    }

    /**
     * Compares two BriefingGeneralStylesModels for equality
     */
    equals(other: BriefingGeneralStylesModel): boolean {
        if (other === null || other === undefined) {
            return false;
        }
        if (!this.mention.equals(other.mention, false)) {
            return false;
        }
        if (!this.title.equals(other.title, false)) {
            return false;
        }
        if (!this.text.equals(other.text, false)) {
            return false;
        }
        if (!this.date.equals(other.date, false)) {
            return false;
        }
        if (!this.tableOfContent.equals(other.tableOfContent, false)) {
            return false;
        }
        if (!this.image.equals(other.image)) {
            return false;
        }
        if (!this.oneColumnLayout.equals(other.oneColumnLayout, false)) {
            return false;
        }
        if (!this.twoColumnLayout.equals(other.twoColumnLayout, false)) {
            return false;
        }
        if (!this.threeColumnLayout.equals(other.threeColumnLayout, false)) {
            return false;
        }
        if (!this.analysis.equals(other.analysis, false)) {
            return false;
        }
        if (!this.divider.equals(other.divider)) {
            return false;
        }
        if (!this.link.equals(other.link, false)) {
            return false;
        }
        if (!this.coverageCurator.equals(other.coverageCurator, false)) {
            return false;
        }

        if (this.accentColor !== other.accentColor) {
            return false;
        }

        return true;
    }
}
